import { FunctionComponent } from 'react'
import {
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
} from '@black-bear-energy/rucksack'
import { CardContentField } from './CardContentField'

/**
 * A text field paired with a "Not applicable" checkbox, to clearly
 * distinguish between an unanswered/unknown value and a value
 * that is known to be empty.
 */
export const EditableTextWithNAOption: FunctionComponent<
  EditableTextWithNAOptionProps
> = ({
  label,
  currentValue,
  currentApplicable,
  errorMsg,
  isEditing,
  isFetching,
  onChange,
}) => {
  const isNotApplicable = currentApplicable === false

  if (!isEditing) {
    return (
      <CardContentField
        label={label}
        value={isNotApplicable ? 'Not applicable' : currentValue}
        isFetching={isFetching}
        isDisabled={isNotApplicable}
      />
    )
  }

  function handleApplicableChange() {
    // if both values are reset to "unknown", update both values to null
    if (!currentValue && currentApplicable == false) {
      onChange({
        isApplicable: null,
        value: null,
      })
      return
    }
    // if the current state is "unknown" (null) and the box is checked,
    // make sure the new state is "Not applicable"
    const newApplicable = currentApplicable == null ? false : !currentApplicable
    onChange({
      isApplicable: newApplicable,
      value: currentValue,
    })
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <TextField
        variant="outlined"
        size="medium"
        label={label}
        value={currentValue ?? ''}
        disabled={isNotApplicable}
        onChange={(input) => {
          const newValue = input.target.value
          onChange({
            value: newValue ? newValue : null,
            isApplicable: newValue ? true : null,
          })
        }}
        error={errorMsg != null}
        helperText={errorMsg}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={isNotApplicable}
            slotProps={{ input: { 'aria-label': `${label} not applicable` } }}
            onChange={handleApplicableChange}
          />
        }
        label={'Not applicable'}
      />
    </Box>
  )
}

export interface EditableTextWithNAOptionProps {
  label: string
  currentValue?: string | null
  currentApplicable?: boolean | null
  errorMsg: string | null
  isEditing: boolean
  isFetching: boolean
  onChange: (next: {
    value?: string | null
    isApplicable: boolean | null
  }) => void
}
